import { useEffect, useMemo, useState } from "react";
import { useCatalog } from "../hooks/useCatalog";
import { slugToLabel } from "../lib/format";
import { navigateTo } from "../lib/navigation";
import { Button } from "../components/Button";
import { ProductCard } from "../components/ProductCard";
import { SectionHeader } from "../components/SectionHeader";

function readMenuFilters() {
  const params = new URLSearchParams(window.location.search);
  return {
    category: params.get("category") ?? "all",
    search: params.get("search") ?? ""
  };
}

export function MenuPage() {
  const { menu } = useCatalog();
  const categories = useMemo(() => ["all", ...new Set(menu.map((item) => item.category))], [menu]);
  const [filters, setFilters] = useState(readMenuFilters);
  const filtered = menu.filter((item) => {
    const categoryMatch =
      filters.category === "all" ||
      item.category.includes(filters.category) ||
      item.tags.some((tag) => tag.includes(filters.category));
    const searchTerm = filters.search.trim().toLowerCase();
    const searchMatch =
      !searchTerm ||
      [item.name, item.subtitle, item.description, item.category, ...item.tags].join(" ").toLowerCase().includes(searchTerm);
    return categoryMatch && searchMatch;
  });

  useEffect(() => {
    const sync = () => setFilters(readMenuFilters());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  function chooseCategory(category: string) {
    setFilters({ category, search: "" });
    navigateTo(category === "all" ? "/menus" : `/menus?category=${encodeURIComponent(category)}`);
  }

  function clearFilters() {
    setFilters({ category: "all", search: "" });
    navigateTo("/menus");
  }

  return (
    <main className="page-shell">
      <SectionHeader eyebrow="Seasonal chef-led plates" title={filters.search ? `Menu Search: ${filters.search}` : "Menus"} centered level={1} />
      <div className="filter-row">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={filters.category === category && !filters.search ? "active" : ""}
            onClick={() => chooseCategory(category)}
          >
            {slugToLabel(category)}
          </button>
        ))}
      </div>
      {filtered.length ? (
        <div className="catalog-grid">
          {filtered.map((item) => (
            <ProductCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <h2>No menu items found</h2>
          <p>Try another search or browse the full menu.</p>
          <Button onClick={clearFilters}>Clear filters</Button>
        </div>
      )}
    </main>
  );
}
